/**
 * setupDataMapper.js
 *
 * Funzione di mapping PURA (read-only) che trasforma la struttura Firestore
 * esistente in una struttura spaziale per-angolo.
 *
 * NON scrive mai su Firestore. La struttura DB rimane invariata.
 * Retrocompatibile al 100% con tutti i setup già salvati.
 */

/**
 * Mappa i dati setup dalla struttura Firestore originale
 * (anteriore/posteriore/gomme_ant_sx/...) alla struttura spaziale (FL/FR/RL/RR).
 *
 * @param {object} setupData - Il campo setupData di un documento Firestore setupSheets
 * @returns {{ FL, FR, RL, RR }} - Dati per angolo
 */
export function mapToCorners(setupData) {
  const ant    = setupData?.anteriore || {};
  const antAmm = ant.ammortizzatori || {};
  const post    = setupData?.posteriore || {};
  const postAmm = post.ammortizzatori || {};
  const aero    = setupData?.aero || {};
  const weights = setupData?.weights || {};

  const buildCorner = ({ axle, side, gomme, amm, hasCaster, rideHeightField, weightKey }) => ({
    // Geometria
    toe:           axle[`convergenza${side}`],
    toeDirection:  axle[`toeInOrOut${side}`],
    camber:        axle[`camber${side}`],
    ...(hasCaster ? { caster: axle[`caster${side}`] } : {}),
    ...(rideHeightField ? { rideHeight: aero[rideHeightField] } : {}),
    // Molla & Bumpstop
    spring:        axle[`molla${side}`],
    bumpstopRate:  axle[`rigidezzaBumpstop${side}`],
    bumpstopRange: axle[`distanzaBumpstop${side}`],
    // Ammortizzatori
    bumpSlow:      amm[`bump${side}`],
    reboundSlow:   amm[`rebound${side}`],
    bumpFast:      amm[`fastBump${side}`],
    reboundFast:   amm[`fastRebound${side}`],
    // Gomma
    pressureCold:  gomme?.pFreddo,
    pressureHot:   gomme?.pCaldoTarget,
    tireCode:       gomme?.codicePneumatico,
    tireId:         gomme?.tireId,
    tireBrand:      gomme?.tireBrand,
    tireCompound:   gomme?.tireCompound,
    tireDimensions: gomme?.tireDimensions,
    // Peso angolo
    cornerWeight:  weights[weightKey],
  });

  return {
    FL: buildCorner({
      axle: ant, side: 'SX',
      gomme: setupData?.gomme_ant_sx,
      amm: antAmm,
      hasCaster: true,
      rideHeightField: 'altezzaTargetSX',
      weightKey: 'cornerFL',
    }),
    FR: buildCorner({
      axle: ant, side: 'DX',
      gomme: setupData?.gomme_ant_dx,
      amm: antAmm,
      hasCaster: true,
      rideHeightField: 'altezzaTargetDX',
      weightKey: 'cornerFR',
    }),
    RL: buildCorner({
      axle: post, side: 'SX',
      gomme: setupData?.gomme_post_sx,
      amm: postAmm,
      hasCaster: false,
      rideHeightField: null,
      weightKey: 'cornerRL',
    }),
    RR: buildCorner({
      axle: post, side: 'DX',
      gomme: setupData?.gomme_post_dx,
      amm: postAmm,
      hasCaster: false,
      rideHeightField: null,
      weightKey: 'cornerRR',
    }),
  };
}

/**
 * Chiavi Firestore originali per ogni campo di ogni angolo.
 * Usato da CornerCard per verificare hiddenFields del template.
 */
export const CORNER_ORIGINAL_KEYS = {
  FL: {
    toe: 'convergenzaSX', toeDirection: 'toeInOrOutSX',
    camber: 'camberSX', caster: 'casterSX', rideHeight: 'altezzaTargetSX',
    spring: 'mollaSX', bumpstopRate: 'rigidezzaBumpstopSX', bumpstopRange: 'distanzaBumpstopSX',
    bumpSlow: 'bumpSX', reboundSlow: 'reboundSX', bumpFast: 'fastBumpSX', reboundFast: 'fastReboundSX',
  },
  FR: {
    toe: 'convergenzaDX', toeDirection: 'toeInOrOutDX',
    camber: 'camberDX', caster: 'casterDX', rideHeight: 'altezzaTargetDX',
    spring: 'mollaDX', bumpstopRate: 'rigidezzaBumpstopDX', bumpstopRange: 'distanzaBumpstopDX',
    bumpSlow: 'bumpDX', reboundSlow: 'reboundDX', bumpFast: 'fastBumpDX', reboundFast: 'fastReboundDX',
  },
  RL: {
    toe: 'convergenzaSX', toeDirection: 'toeInOrOutSX',
    camber: 'camberSX',
    spring: 'mollaSX', bumpstopRate: 'rigidezzaBumpstopSX', bumpstopRange: 'distanzaBumpstopSX',
    bumpSlow: 'bumpSX', reboundSlow: 'reboundSX', bumpFast: 'fastBumpSX', reboundFast: 'fastReboundSX',
  },
  RR: {
    toe: 'convergenzaDX', toeDirection: 'toeInOrOutDX',
    camber: 'camberDX',
    spring: 'mollaDX', bumpstopRate: 'rigidezzaBumpstopDX', bumpstopRange: 'distanzaBumpstopDX',
    bumpSlow: 'bumpDX', reboundSlow: 'reboundDX', bumpFast: 'fastBumpDX', reboundFast: 'fastReboundDX',
  },
};

/**
 * Quale modulo template "possiede" ogni chiave campo originale.
 * Usato per sapere se un modulo è attivo nel template prima di mostrare i campi.
 */
export const FIELD_MODULE_OWNERSHIP = {
  convergenzaSX: 'alignment', convergenzaDX: 'alignment',
  toeInOrOutSX: 'alignment', toeInOrOutDX: 'alignment',
  camberSX: 'alignment', camberDX: 'alignment',
  casterSX: 'alignment', casterDX: 'alignment',
  mollaSX: 'mechanical_grip', mollaDX: 'mechanical_grip',
  rigidezzaBumpstopSX: 'mechanical_grip', rigidezzaBumpstopDX: 'mechanical_grip',
  distanzaBumpstopSX: 'mechanical_grip', distanzaBumpstopDX: 'mechanical_grip',
  bumpSX: 'dampers', bumpDX: 'dampers',
  reboundSX: 'dampers', reboundDX: 'dampers',
  fastBumpSX: 'dampers', fastBumpDX: 'dampers',
  fastReboundSX: 'dampers', fastReboundDX: 'dampers',
  altezzaTargetSX: 'aero', altezzaTargetDX: 'aero',
};

/**
 * Estrae la configurazione dei campi dal template modules array.
 *
 * @param {Array} templateModules - setupTemplate.modules
 * @returns {{ isHidden, isModuleActive, customFields }}
 */
export function buildFieldConfig(templateModules) {
  const hiddenSet = new Set();
  const activeModules = new Set();
  const customByPosition = { FL: [], FR: [], RL: [], RR: [], General: [] };

  (templateModules || []).forEach(module => {
    activeModules.add(module.key);
    (module.config?.hiddenFields || []).forEach(f => hiddenSet.add(f));
    (module.config?.customFields || []).forEach(cf => {
      const pos = cf.position || 'General';
      const bucket = Object.prototype.hasOwnProperty.call(customByPosition, pos) ? pos : 'General';
      customByPosition[bucket].push(cf);
    });
  });

  return {
    isHidden: (fieldKey) => hiddenSet.has(fieldKey),
    isModuleActive: (moduleKey) => activeModules.has(moduleKey),
    customFields: customByPosition,
  };
}

/**
 * Cerca il valore di un custom field nel setupData, controllando le
 * posizioni più probabili per ogni angolo.
 */
export function getCustomFieldValue(setupData, field, position) {
  if (!field?.key) return undefined;
  const k = field.key;

  const searchPaths = {
    FL: [
      setupData?.anteriore?.[k],
      setupData?.anteriore?.ammortizzatori?.[k],
      setupData?.gomme_ant_sx?.[k],
    ],
    FR: [
      setupData?.anteriore?.[k],
      setupData?.anteriore?.ammortizzatori?.[k],
      setupData?.gomme_ant_dx?.[k],
    ],
    RL: [
      setupData?.posteriore?.[k],
      setupData?.posteriore?.ammortizzatori?.[k],
      setupData?.gomme_post_sx?.[k],
    ],
    RR: [
      setupData?.posteriore?.[k],
      setupData?.posteriore?.ammortizzatori?.[k],
      setupData?.gomme_post_dx?.[k],
    ],
    General: [
      setupData?.differenziale?.[k],
      setupData?.freni?.[k],
      setupData?.aero?.[k],
      setupData?.elettronica?.[k],
      setupData?.weights?.[k],
      setupData?.strategia?.[k],
      setupData?.feedback?.[k],
    ],
  };

  const paths = searchPaths[position] || searchPaths.General;
  for (const val of paths) {
    if (val !== null && val !== undefined && val !== '') return val;
  }
  return undefined;
}
